import AssignmentInd from "@mui/icons-material/AssignmentInd";
import ExpandMore from "@mui/icons-material/ExpandMore";
import LabelImportant from "@mui/icons-material/LabelImportant";
import StickyNote2Outlined from "@mui/icons-material/StickyNote2Outlined";
import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  useTheme,
} from "@mui/material";
import { useCallback, useState } from "react";

import type { Row } from "../columns";

import { epcraBulkActionContext, type EpcraBulkDialogState } from "./bulkActionContext";
import { BulkUpdateAssigneeDialog } from "./BulkUpdateAssigneeDialog";
import { BulkUpdateNotesDialog } from "./BulkUpdateNotesDialog";
import { BulkUpdateStatusDialog } from "./BulkUpdateStatusDialog";
import {
  BulkVerifyCredentialsDialog,
  BulkVerifyCredentialsMenuItem,
} from "./BulkVerifyCredentialsDialog";

const buttonId = "epcra-ci-bulk-actions-button";
const menuId = "epcra-ci-bulk-actions-menu";

export function EpcraBulkActionsMenu({
  selectedRows,
  onClearSelection,
}: {
  selectedRows: Row[];
  onClearSelection: () => void;
}) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dialogState, setDialogState] = useState<EpcraBulkDialogState>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuClose = useCallback((_event: object, reason?: string) => {
    setAnchorEl(null);
    if (reason !== "menuItemClick") {
      setDialogState(null);
    }
  }, []);

  const handleMenuButtonClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const onDialogClose = useCallback(() => {
    setAnchorEl(null);
    setDialogState(null);
  }, []);

  const onSuccess = useCallback(() => {
    onClearSelection();
  }, [onClearSelection]);

  return (
    <epcraBulkActionContext.Provider
      value={{
        selectedRows,
        dialogState,
        setDialogState,
        onClose: onDialogClose,
        onSuccess,
      }}
    >
      <Button
        id={buttonId}
        size="medium"
        aria-controls={menuOpen ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={menuOpen ? "true" : undefined}
        disabled={!selectedRows.length}
        variant="contained"
        onClick={handleMenuButtonClick}
        endIcon={<ExpandMore />}
        sx={{
          whiteSpace: "nowrap",
          backgroundColor: theme.palette.secondary.main,
        }}
      >
        Bulk Actions ({selectedRows.length})
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        MenuListProps={{ "aria-labelledby": buttonId }}
      >
        <MenuList>
          <MenuItem onClick={() => setDialogState("assignee")} disabled={selectedRows.length === 0}>
            <ListItemIcon>
              <AssignmentInd fontSize="small" />
            </ListItemIcon>
            <ListItemText>Update assignee…</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => setDialogState("status")} disabled={selectedRows.length === 0}>
            <ListItemIcon>
              <LabelImportant fontSize="small" />
            </ListItemIcon>
            <ListItemText>Update implementation status…</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => setDialogState("notes")} disabled={selectedRows.length === 0}>
            <ListItemIcon>
              <StickyNote2Outlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>Update notes…</ListItemText>
          </MenuItem>
          <BulkVerifyCredentialsMenuItem />
        </MenuList>
      </Menu>
      <BulkUpdateAssigneeDialog />
      <BulkUpdateStatusDialog />
      <BulkUpdateNotesDialog />
      <BulkVerifyCredentialsDialog />
    </epcraBulkActionContext.Provider>
  );
}
